import { useCallback, useState } from 'react'

/**
 * Custom hook for handling pagination
 * @param {Object} config - Configuration object
 * @param {number} config.initialPage - Initial page number (default: 1)
 * @param {number} config.initialLimit - Initial items per page (default: 20)

 * @returns {Object} Pagination state and handlers
 */
const usePagination = (config = {}) => {
  const { initialPage = 1, initialLimit = 20 } = config

  const [paginationState, setPaginationState] = useState({
    page: initialPage,
    limit: initialLimit,
    totalPages: 0,
    totalItems: 0,
  })

  // Go to specific page
  const goToPage = useCallback((pageNumber) => {
    setPaginationState((prev) => ({
      ...prev,
      page: pageNumber,
    }))
  }, [])

  // Go to next page
  const nextPage = useCallback(() => {
    setPaginationState((prev) => ({
      ...prev,
      page: prev.page < prev.totalPages ? prev.page + 1 : prev.page,
    }))
  }, [])

  // Go to previous page
  const prevPage = useCallback(() => {
    setPaginationState((prev) => ({
      ...prev,
      page: prev.page > 1 ? prev.page - 1 : prev.page,
    }))
  }, [])

  // Go to first page
  const firstPage = useCallback(() => {
    setPaginationState((prev) => ({
      ...prev,
      page: 1,
    }))
  }, [])

  // Go to last page
  const lastPage = useCallback(() => {
    setPaginationState((prev) => ({
      ...prev,
      page: prev.totalPages,
    }))
  }, [])

  // Change items per page
  const changeLimit = useCallback((newLimit) => {
    setPaginationState((prev) => ({
      ...prev,
      limit: newLimit,
      page: 1, // Reset to first page when limit changes
    }))
  }, [])

  // Set total pages (call this after receiving API response)
  const setTotalPages = useCallback((total) => {
    setPaginationState((prev) => ({
      ...prev,
      totalPages: total,
    }))
  }, [])

  // Set total items (call this after receiving API response)
  const setTotalItems = useCallback((total) => {
    setPaginationState((prev) => ({
      ...prev,
      totalItems: total,
      totalPages: Math.ceil(total / prev.limit),
    }))
  }, [])

  // Reset pagination to initial state
  const resetPagination = useCallback(() => {
    setPaginationState({
      page: initialPage,
      limit: initialLimit,
      totalPages: 0,
      totalItems: 0,
    })
  }, [initialPage, initialLimit])

  // Get query params for API call
  const getQueryParams = useCallback(() => {
    return {
      page: paginationState.page,
      limit: paginationState.limit,
    }
  }, [paginationState])

  // Check if there's a next page
  const hasNextPage = paginationState.page < paginationState.totalPages

  // Check if there's a previous page
  const hasPrevPage = paginationState.page > 1

  return {
    // State
    page: paginationState.page,
    limit: paginationState.limit,
    totalPages: paginationState.totalPages,
    totalItems: paginationState.totalItems,
    hasNextPage,
    hasPrevPage,

    // Actions
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    changeLimit,
    setTotalPages,
    setTotalItems,
    resetPagination,
    getQueryParams,
  }
}

export default usePagination
